import type { FlexProps } from '@chakra-ui/react';
import { Flex, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useRef } from 'react';
import { useIntersection } from 'react-use';

export type InfiniteLoaderProps = FlexProps & {
  hasMore: boolean;
  onNext: () => Promise<unknown>;
  root?: IntersectionObserverInit['root'];
  rootMargin?: IntersectionObserverInit['rootMargin'];
};

export const InfiniteLoader = ({
  children,
  hasMore,
  onNext,
  root = null,
  rootMargin = '0px 0px 50% 0px',
  ...props
}: InfiniteLoaderProps) => {
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const loaderInterception = useIntersection(loaderRef, {
    root,
    rootMargin,
    threshold: useMemo(() => {
      return [...Array(11)].map((_, i) => i * 0.1);
    }, []),
  });
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  const isLoadingRef = useRef(false);

  useEffect(() => {
    if (
      hasMore &&
      !isLoadingRef.current &&
      loaderInterception?.intersectionRatio &&
      loaderInterception.intersectionRatio > 0
    ) {
      isLoadingRef.current = true;
      onNextRef.current().finally(() => {
        isLoadingRef.current = false;
      });
    }
  }, [loaderInterception?.intersectionRatio, hasMore, onNextRef]);

  return (
    <Flex
      ref={loaderRef}
      minH="50px"
      opacity={hasMore ? 1 : 0}
      justify="center"
      alignItems="center"
      {...props}
    >
      {children || (
        <Flex alignItems="center" gap="8px">
          <Text>Loading...</Text>
        </Flex>
      )}
    </Flex>
  );
};
